import { useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ImageUpload } from "@/components/ImageUpload";
import { toast } from "sonner";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");

function buildEmpty(fields) {
  const o = {};
  fields.forEach((f) => {
    if (f.type === "checkbox") o[f.key] = f.default ?? true;
    else if (f.type === "gallery") o[f.key] = [];
    else o[f.key] = f.default ?? "";
  });
  return o;
}

function ContentForm({ open, onOpenChange, editing, onSaved, config }) {
  const { fields, endpoint, idField, singular } = config;
  const [form, setForm] = useState(buildEmpty(fields));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      const filled = {};
      fields.forEach((f) => {
        let v = editing[f.key];
        if (f.type === "tags" && Array.isArray(v)) v = v.join(", ");
        filled[f.key] = v ?? (f.type === "checkbox" ? true : f.type === "gallery" ? [] : "");
      });
      setForm(filled);
    } else {
      setForm(buildEmpty(fields));
    }
  }, [editing, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      fields.forEach((f) => {
        if (f.type === "tags") {
          payload[f.key] = String(payload[f.key] || "").split(",").map((t) => t.trim()).filter(Boolean);
        }
        if (f.type === "number") {
          payload[f.key] = Number(payload[f.key]) || 0;
        }
      });
      if (editing) await api.put(`/${endpoint}/${editing[idField]}`, payload);
      else await api.post(`/${endpoint}`, payload);
      toast.success(editing ? `${singular} updated` : `${singular} created`);
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-brand-bg max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl text-brand">{editing ? `Edit ${singular}` : `New ${singular}`}</DialogTitle>
          <DialogDescription className="text-brand-muted">Fill in the details below. Markdown is supported in the body.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {fields.map((f) => (
            <div key={f.key}>
              {f.type === "checkbox" ? (
                <label className="flex items-center gap-2 text-sm text-brand">
                  <input data-testid={`cf-${f.key}`} type="checkbox" checked={!!form[f.key]} onChange={(e) => set(f.key, e.target.checked)} className="w-4 h-4 accent-[#1A3B2A]" />
                  {f.label}
                </label>
              ) : (
                <>
                  <Label className="text-brand">{f.label}{f.hint && <span className="text-brand-muted font-normal"> — {f.hint}</span>}</Label>
                  {f.type === "image" ? (
                    <div className="mt-1.5"><ImageUpload value={form[f.key]} onChange={(v) => set(f.key, v)} testid={`cf-${f.key}`} /></div>
                  ) : f.type === "gallery" ? (
                    <div className="mt-1.5" data-testid={`cf-${f.key}`}>
                      {(form[f.key] || []).length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-2">
                          {(form[f.key] || []).map((src, i) => (
                            <div key={src} className="relative">
                              <img src={src} alt={`img ${i + 1}`} className="w-16 h-16 object-cover rounded-sm border border-brand/10" />
                              <button type="button" data-testid={`cf-${f.key}-remove-${i}`}
                                onClick={() => set(f.key, (form[f.key] || []).filter((_, idx) => idx !== i))}
                                className="absolute -top-2 -right-2 bg-brand-terracotta text-white rounded-full p-1 shadow hover:bg-brand-hover">
                                <span className="block w-3 h-3 leading-3 text-[10px]">✕</span>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <ImageUpload value="" onChange={(url) => url && set(f.key, [...(form[f.key] || []), url])} testid={`cf-${f.key}-add`} />
                    </div>
                  ) : f.type === "textarea" || f.type === "markdown" ? (
                    <Textarea data-testid={`cf-${f.key}`} value={form[f.key]} onChange={(e) => set(f.key, e.target.value)}
                      rows={f.type === "markdown" ? 12 : 3} className="mt-1.5 rounded-sm font-mono text-sm" placeholder={f.placeholder} />
                  ) : f.type === "select" ? (
                    <Select value={form[f.key]} onValueChange={(v) => set(f.key, v)}>
                      <SelectTrigger data-testid={`cf-${f.key}`} className="mt-1.5 h-11 rounded-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <Input data-testid={`cf-${f.key}`} type={f.type === "date" ? "date" : f.type === "number" ? "number" : "text"} value={form[f.key]}
                      onChange={(e) => set(f.key, e.target.value)} className="mt-1.5 h-11 rounded-sm" placeholder={f.placeholder} />
                  )}
                </>
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button data-testid="cf-save" onClick={save} disabled={saving} className="bg-brand hover:bg-brand-hover text-white rounded-full">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ContentManager({ config }) {
  const { endpoint, adminEndpoint, idField, singular, plural, titleKey } = config;
  const sid = singular.toLowerCase().replace(/\s+/g, "-");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get(`/${adminEndpoint}`).then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm(`Delete this ${singular.toLowerCase()}?`)) return;
    await api.delete(`/${endpoint}/${id}`);
    toast.success(`${singular} deleted`);
    load();
  };

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-brand" /></div>;

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button data-testid={`add-${sid}-btn`} onClick={() => { setEditing(null); setOpen(true); }}
          className="bg-brand hover:bg-brand-hover text-white rounded-full gap-1.5"><Plus className="w-4 h-4" /> New {singular}</Button>
      </div>
      {items.length === 0 ? (
        <p className="text-brand-muted py-10 text-center">No {plural.toLowerCase()} yet.</p>
      ) : (
        <div className="grid gap-3" data-testid={`admin-${plural.toLowerCase()}`}>
          {items.map((it) => (
            <div key={it[idField]} className="bg-white border border-brand/10 rounded-sm p-4 flex items-center gap-4" data-testid={`admin-${sid}-${it[idField]}`}>
              {it[config.imageKey || "cover_image"] && <img src={it[config.imageKey || "cover_image"]} alt="" className="w-14 h-14 object-cover rounded-sm bg-brand-surface" />}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-brand truncate">
                  {it[titleKey]}
                  {!it.published && <span className="text-[10px] bg-brand-terracotta/15 text-brand-terracotta px-2 py-0.5 rounded-full ml-2">Draft</span>}
                </p>
                <p className="text-xs text-brand-muted mt-0.5">{config.listSubtitle ? config.listSubtitle(it) : `${fmtDate(it.created_at)}${it.employment_type ? ` · ${it.employment_type}` : ""}${it.location ? ` · ${it.location}` : ""}`}</p>
              </div>
              <button data-testid={`edit-${sid}-${it[idField]}`} onClick={() => { setEditing(it); setOpen(true); }} className="p-2 text-brand hover:bg-brand-surface rounded-full"><Pencil className="w-4 h-4" /></button>
              <button data-testid={`delete-${sid}-${it[idField]}`} onClick={() => remove(it[idField])} className="p-2 text-brand-terracotta hover:bg-brand-surface rounded-full"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
      <ContentForm open={open} onOpenChange={setOpen} editing={editing} onSaved={load} config={config} />
    </div>
  );
}

export const BLOG_CONFIG = {
  endpoint: "blogs", adminEndpoint: "admin/blogs", idField: "blog_id", singular: "Blog Post", plural: "Blog Posts", titleKey: "title",
  fields: [
    { key: "title", label: "Title", type: "text" },
    { key: "excerpt", label: "Excerpt", type: "textarea", hint: "short summary for cards" },
    { key: "cover_image", label: "Cover Image", type: "image" },
    { key: "author", label: "Author", type: "text", default: "Yayra Glover Organic Inputs" },
    { key: "tags", label: "Tags", type: "tags", hint: "comma separated" },
    { key: "body", label: "Body", type: "markdown", hint: "Markdown supported (# heading, **bold**, - list, > quote)", placeholder: "## Section heading\n\nWrite your story here..." },
    { key: "gallery", label: "More Pictures", type: "gallery", hint: "extra photos shown as a gallery" },
    { key: "published", label: "Published (visible on site)", type: "checkbox", default: true },
  ],
};

export const NEWS_CONFIG = {
  endpoint: "news", adminEndpoint: "admin/news", idField: "news_id", singular: "News Article", plural: "News Articles", titleKey: "title",
  fields: [
    { key: "title", label: "Title", type: "text" },
    { key: "excerpt", label: "Excerpt", type: "textarea", hint: "short summary" },
    { key: "cover_image", label: "Cover Image", type: "image" },
    { key: "body", label: "Body", type: "markdown", hint: "Markdown supported", placeholder: "Write the announcement..." },
    { key: "gallery", label: "More Pictures", type: "gallery", hint: "extra photos shown as a gallery" },
    { key: "published", label: "Published (visible on site)", type: "checkbox", default: true },
  ],
};

export const JOB_CONFIG = {
  endpoint: "jobs", adminEndpoint: "admin/jobs", idField: "job_id", singular: "Job", plural: "Jobs", titleKey: "title",
  fields: [
    { key: "title", label: "Job Title", type: "text" },
    { key: "department", label: "Department", type: "text" },
    { key: "location", label: "Location", type: "text", placeholder: "Suhum, Eastern Region" },
    { key: "employment_type", label: "Employment Type", type: "select", options: ["Full-time", "Part-time", "Contract", "Internship", "Volunteer"], default: "Full-time" },
    { key: "closes_at", label: "Applications Close", type: "date" },
    { key: "description", label: "Description", type: "markdown", hint: "Markdown supported — role, responsibilities, requirements", placeholder: "## About the role\n\n## Responsibilities\n\n- ...\n\n## Requirements\n\n- ..." },
    { key: "published", label: "Published (visible on site)", type: "checkbox", default: true },
  ],
};

export const TEAM_CONFIG = {
  endpoint: "team", adminEndpoint: "admin/team", idField: "team_id", singular: "Team Member", plural: "Team Members", titleKey: "name",
  imageKey: "photo",
  listSubtitle: (it) => `${it.title || ""}${it.group ? ` · ${it.group}` : ""}`,
  fields: [
    { key: "name", label: "Full Name", type: "text" },
    { key: "title", label: "Job Title", type: "text", placeholder: "e.g. Managing Director" },
    { key: "group", label: "Group", type: "select", options: ["Leadership", "Operations & Support", "Zonal Managers"], default: "Leadership" },
    { key: "photo", label: "Photo", type: "image" },
    { key: "bio", label: "Short Bio", type: "textarea", hint: "optional" },
    { key: "linkedin", label: "LinkedIn URL", type: "text", placeholder: "https://linkedin.com/in/…" },
    { key: "email", label: "Email", type: "text" },
    { key: "order", label: "Sort Order", type: "number", hint: "lower shows first within the group", default: 0 },
    { key: "published", label: "Published (visible on site)", type: "checkbox", default: true },
  ],
};
